<script setup>
import {
  NCard, NSpace, NH1, NH2, NH3, NP, NDivider,
  NButton, NIcon, NGrid, NGridItem, NStatistic, NTag, NGradientText
} from 'naive-ui';
import { useI18n } from 'vue-i18n';
import {
  Languages, Users, Scale, Globe, Heart,
  BookOpen, UserPlus, Sparkles, Bookmark, Shield,
  Phone, MessageCircle, Calendar
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

// Stats with Lucide icons
const aboutStats = [
  {
    count: 10,
    label: 'about.stats.languagesSupported',
    icon: Languages,
    color: '#36d1dc'
  },
  {
    count: 2500,
    label: 'about.stats.verifiedLawyers',
    icon: Scale,
    color: '#5b86e5'
  },
  {
    count: 15000,
    label: 'about.stats.legalConsultations',
    icon: Users,
    color: '#ef473a'
  }
];

// Features with relevant icons for legal directory
const features = [
  {
    icon: Languages,
    color: 'multilingual',
    titleKey: 'multilingualTitle',
    textKey: 'multilingualText',
    showLanguages: true
  },
  {
    icon: Shield,
    color: 'verified',
    titleKey: 'verifiedTitle',
    textKey: 'verifiedText',
    showLanguages: false
  },
  {
    icon: Phone,
    color: 'accessible',
    titleKey: 'accessibleTitle',
    textKey: 'accessibleText',
    showLanguages: false
  }
];

// Indian languages supported
const indianLanguages = [
  { name: 'हिन्दी', type: 'success' },
  { name: 'বাংলা', type: 'info' },
  { name: 'తెలుగు', type: 'warning' },
  { name: 'தமிழ்', type: 'error' },
  { name: 'മലയാളം', type: 'success' },
  { name: 'ગુજરાતી', type: 'primary' },
  { name: 'ਪੰਜਾਬੀ', type: 'warning' },
  { name: 'मराठी', type: 'info' },
  { name: 'ಕನ್ನಡ', type: 'error' },
  { name: 'ଓଡ଼ିଆ', type: 'success' }
];
</script>

<template>
  <div class="about-container">
    <!-- Hero Section -->
    <div class="hero-section">
      <NH1 class="hero-title">
        <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
          {{ dt('about.heroTitle') }}
        </NGradientText>
      </NH1>
      <NP class="hero-subtitle">{{ dt('about.heroSubtitle') }}</NP>
    </div>

    <!-- Mission Card -->
    <NCard class="mission-card">
      <div class="mission-content">
        <div class="mission-text">
          <NH2 class="section-heading">{{ dt('about.missionTitle') }}</NH2>
          <NP class="mission-paragraph">{{ dt('about.missionText') }}</NP>
          <NButton type="primary" size="large" class="mission-button">
            {{ dt('about.missionCTA') }}
          </NButton>
        </div>
        <div class="mission-visual">
          <div class="pulse-circle"></div>
        </div>
      </div>
    </NCard>

    <!-- Stats Section -->
    <div class="stats-section">
      <NGrid :cols="3" :x-gap="16" responsive="screen" :collapsed="['xs', 's']">
        <NGridItem v-for="(stat, index) in aboutStats" :key="index">
          <NStatistic :label="dt(stat.label)" :value="stat.count">
            <template #prefix>
              <component :is="stat.icon" :size="24" :color="stat.color" />
            </template>
          </NStatistic>
        </NGridItem>
      </NGrid>
    </div>

    <!-- Features Section -->
    <div class="features-section">
      <NH2 class="section-title">{{ dt('about.featuresTitle') }}</NH2>
      <div class="features-grid">
        <div class="feature-item" v-for="(feature, index) in features" :key="index">
          <div :class="`feature-icon ${feature.color}`">
            <component :is="feature.icon" size="32" color="#ffffff" />
          </div>
          <NH3 class="feature-heading">{{ dt(`about.features.${feature.titleKey}`) }}</NH3>
          <NP class="feature-text">{{ dt(`about.features.${feature.textKey}`) }}</NP>
          <div v-if="feature.showLanguages" class="language-tags">
            <NTag v-for="(lang, i) in indianLanguages" :key="i" :type="lang.type" size="small">
              {{ lang.name }}
            </NTag>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works -->
    <div class="how-it-works">
      <NH2 class="section-title">{{ dt('about.howItWorksTitle') }}</NH2>
      <div class="steps-container">
        <div class="step">
          <div class="step-number">1</div>
          <NH3 class="step-heading">{{ dt('about.steps.step1Title') }}</NH3>
          <NP class="step-text">{{ dt('about.steps.step1Text') }}</NP>
        </div>
        <div class="step-connector"></div>
        <div class="step">
          <div class="step-number">2</div>
          <NH3 class="step-heading">{{ dt('about.steps.step2Title') }}</NH3>
          <NP class="step-text">{{ dt('about.steps.step2Text') }}</NP>
        </div>
        <div class="step-connector"></div>
        <div class="step">
          <div class="step-number">3</div>
          <NH3 class="step-heading">{{ dt('about.steps.step3Title') }}</NH3>
          <NP class="step-text">{{ dt('about.steps.step3Text') }}</NP>
        </div>
      </div>
    </div>

    <!-- Join Section -->
    <div class="join-section">
      <NCard class="join-card">
        <NH2 class="join-heading">{{ dt('about.joinTitle') }}</NH2>
        <NP class="join-text">{{ dt('about.joinText') }}</NP>
        <div class="button-container">
          <NButton type="primary" size="large" class="join-button">
            {{ dt('about.findLawyerCTA') }}
          </NButton>
          <NButton size="large" class="join-button-secondary">
            {{ dt('about.joinAsLawyerCTA') }}
          </NButton>
        </div>
      </NCard>
    </div>
  </div>
</template>

<style scoped>
.about-container {
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  color: #333;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  padding: 40px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
}

.mission-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
}

.mission-content {
  display: flex;
  align-items: center;
  min-height: 250px;
}

.mission-text {
  flex: 1;
  padding: 24px;
}

.mission-paragraph {
  font-size: 1rem;
  line-height: 1.6;
  margin: 16px 0;
}

.mission-visual {
  flex: 1;
  position: relative;
  height: 250px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  animation: pulse 2s infinite;
}

.pulse-circle::after {
  content: "";
  position: absolute;
  width: 70%;
  height: 70%;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  top: 15%;
  left: 15%;
}

.mission-button {
  margin-top: 16px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
}

.stats-section {
  margin: 40px 0;
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  font-size: 1.5rem;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin: 30px 0 40px;
}

.feature-item {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.multilingual {
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.verified {
  background: linear-gradient(135deg, #11998e, #38ef7d);
}

.accessible {
  background: linear-gradient(135deg, #fc4a1a, #f7b733);
}

.language-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.how-it-works {
  margin: 40px 0;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
}

.step {
  flex: 1;
  text-align: center;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
}

.step-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0 auto 16px;
}

.step-connector {
  flex: 0.5;
  height: 3px;
  background: linear-gradient(90deg, #36d1dc, #5b86e5);
  position: relative;
  z-index: 0;
}

.join-section {
  margin: 40px 0;
}

.join-card {
  text-align: center;
  padding: 30px 20px;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1), rgba(91, 134, 229, 0.1));
  border-radius: 16px;
}

.join-text {
  max-width: 700px;
  margin: 16px auto 24px auto;
  font-size: 1rem;
}

.button-container {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.join-button {
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
  font-size: 1rem;
  padding: 0 30px;
  height: 48px;
  min-width: 160px;
}

.join-button-secondary {
  background: transparent;
  border: 2px solid #36d1dc;
  color: #36d1dc;
  font-size: 1rem;
  padding: 0 30px;
  height: 48px;
  min-width: 160px;
}

.join-button-secondary:hover {
  background: #36d1dc;
  color: white;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@media screen and (max-width: 768px) {
  .mission-content {
    flex-direction: column;
  }

  .mission-visual {
    width: 100%;
    height: 200px;
  }

  .steps-container {
    flex-direction: column;
    gap: 20px;
  }

  .step-connector {
    width: 3px;
    height: 20px;
  }

  .button-container {
    flex-direction: column;
    align-items: center;
  }

  .join-button,
  .join-button-secondary {
    width: 100%;
    max-width: 250px;
  }
}
</style>